import { useState, useRef, useEffect } from "react";
import DSBox from "../../views/system-design/DSBox";
import DSIconButton from "../../views/system-design/DSIconButton";
import DSTypography from "../../views/system-design/DSTypography";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { formatSecondsToMMSS } from "../../utils/formatTime";

interface AudioPlayerProps {
  src: string;
  title?: string;
  duration?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

const AudioPlayer = ({
  src,
  title,
  duration,
  onPlay,
  onPause,
  onEnded,
}: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      if (!duration) {
        setAudioDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (onEnded) onEnded();
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [duration, onEnded]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      if (onPause) onPause();
    } else {
      audio.play().catch((error) => {
        console.error(`Failed to play audio file (src: ${src}):`, error);
      });
      setIsPlaying(true);
      if (onPlay) onPlay();
    }
  };

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(event.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  return (
    <DSBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1,
        backgroundColor: "white.8",
        borderRadius: 1,
        width: "100%",
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <DSIconButton onClick={handlePlayPause} size="small">
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </DSIconButton>

      {title && (
        <DSTypography variant="body2" sx={{ minWidth: "100px" }}>
          {title}
        </DSTypography>
      )}

      <DSBox sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <input
          type="range"
          min={0}
          max={audioDuration || 100}
          value={currentTime}
          onChange={handleSliderChange}
          style={{
            width: "100%",
            height: "4px",
            borderRadius: "2px",
            outline: "none",
          }}
        />
      </DSBox>

      <DSTypography variant="body2" sx={{ minWidth: "90px" }}>
        {formatSecondsToMMSS(currentTime)} /{" "}
        {formatSecondsToMMSS(audioDuration)}
      </DSTypography>
    </DSBox>
  );
};

export default AudioPlayer;
